import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Target, Plus, Trash2, CheckCircle2, Circle, Sparkles,
  Loader2, Calendar, ImageIcon, MessageCircle, X,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

type Goal = {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  target_date: string | null;
  vision_image_url: string | null;
  status: string;
  progress: number;
  created_at: string;
};

type Milestone = {
  id: string;
  goal_id: string;
  title: string;
  due_date: string | null;
  completed: boolean;
  sort_order: number;
};

type Nudge = {
  id: string;
  goal_id: string | null;
  message: string;
  tone: string;
  read: boolean;
  created_at: string;
};

const CATEGORIES = ["Career", "Skill", "Education", "Health", "Personal", "Project"];

export default function Goals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [milestonesByGoal, setMilestonesByGoal] = useState<Record<string, Milestone[]>>({});
  const [nudges, setNudges] = useState<Nudge[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [nudgingId, setNudgingId] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: "", description: "", category: "Career", target_date: "",
    vision_image_url: "", milestonesText: "",
  });

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const [g, n] = await Promise.all([
      supabase.from("goals").select("*").eq("user_id", user.id).order("created_at", { ascending: false }),
      supabase.from("goal_nudges").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(20),
    ]);
    const goalsData = (g.data as Goal[]) ?? [];
    setGoals(goalsData);
    setNudges((n.data as Nudge[]) ?? []);

    if (goalsData.length) {
      const { data: ms } = await supabase
        .from("milestones").select("*")
        .in("goal_id", goalsData.map((x) => x.id))
        .order("sort_order");
      const byGoal: Record<string, Milestone[]> = {};
      (ms as Milestone[] ?? []).forEach((m) => {
        (byGoal[m.goal_id] = byGoal[m.goal_id] || []).push(m);
      });
      setMilestonesByGoal(byGoal);
    } else {
      setMilestonesByGoal({});
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const recalcProgress = async (goalId: string) => {
    const ms = milestonesByGoal[goalId] || [];
    const total = ms.length;
    const done = ms.filter((m) => m.completed).length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    const status = total > 0 && done === total ? "completed" : "active";
    await supabase.from("goals").update({ progress, status }).eq("id", goalId);
  };

  const createGoal = async () => {
    if (!user || !form.title.trim()) return;
    setCreating(true);
    try {
      const { data: g, error } = await supabase
        .from("goals")
        .insert({
          user_id: user.id,
          title: form.title.trim(),
          description: form.description.trim() || null,
          category: form.category,
          target_date: form.target_date || null,
          vision_image_url: form.vision_image_url.trim() || null,
        })
        .select().single();
      if (error) throw error;

      const lines = form.milestonesText.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length) {
        await supabase.from("milestones").insert(
          lines.map((title, i) => ({
            goal_id: g.id, user_id: user.id, title, sort_order: i,
          }))
        );
      }
      toast.success("Goal added to your vision board");
      setCreateOpen(false);
      setForm({ title: "", description: "", category: "Career", target_date: "", vision_image_url: "", milestonesText: "" });
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleMilestone = async (m: Milestone) => {
    const next = !m.completed;
    setMilestonesByGoal((prev) => ({
      ...prev,
      [m.goal_id]: prev[m.goal_id].map((x) => x.id === m.id ? { ...x, completed: next } : x),
    }));
    await supabase.from("milestones").update({
      completed: next, completed_at: next ? new Date().toISOString() : null,
    }).eq("id", m.id);

    // recalc with fresh state
    const updated = (milestonesByGoal[m.goal_id] || []).map((x) =>
      x.id === m.id ? { ...x, completed: next } : x
    );
    const total = updated.length;
    const done = updated.filter((x) => x.completed).length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    const status = total > 0 && done === total ? "completed" : "active";
    await supabase.from("goals").update({ progress, status }).eq("id", m.goal_id);
    setGoals((g) => g.map((x) => x.id === m.goal_id ? { ...x, progress, status } : x));
  };

  const deleteGoal = async (id: string) => {
    if (!confirm("Delete this goal and all its milestones?")) return;
    await supabase.from("goals").delete().eq("id", id);
    setGoals((g) => g.filter((x) => x.id !== id));
    toast.success("Goal removed");
  };

  const requestNudge = async (goalId: string) => {
    setNudgingId(goalId);
    try {
      const { data, error } = await supabase.functions.invoke("generate-goal-nudge", { body: { goalId } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setNudges((n) => [data.nudge, ...n]);
      toast.success("YOMA sent you a nudge");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setNudgingId(null);
    }
  };

  const dismissNudge = async (id: string) => {
    setNudges((n) => n.filter((x) => x.id !== id));
    await supabase.from("goal_nudges").update({ read: true }).eq("id", id);
  };

  const activeNudges = nudges.filter((n) => !n.read);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
            </Button>
            <WeKitLogo size="sm" />
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 rounded-full"><Plus className="h-4 w-4" /> New Goal</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" /> Add a goal to your vision board
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Become a product designer at a fintech" />
                </div>
                <div>
                  <Label>Why this matters (optional)</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="The story behind this goal..." rows={3} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Target date</Label>
                    <Input type="date" value={form.target_date}
                      onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Vision image URL (optional)</Label>
                  <Input value={form.vision_image_url} onChange={(e) => setForm({ ...form, vision_image_url: e.target.value })}
                    placeholder="https://..." />
                </div>
                <div>
                  <Label>Milestones (one per line)</Label>
                  <Textarea value={form.milestonesText} onChange={(e) => setForm({ ...form, milestonesText: e.target.value })}
                    placeholder={"Learn Figma basics\nShip 3 case studies\nApply to 10 internships"} rows={4} />
                </div>
                <Button onClick={createGoal} disabled={!form.title.trim() || creating}
                  className="w-full rounded-full gap-2">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add to vision board
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Vision Board</h1>
          <p className="text-muted-foreground mt-2">
            Picture it. Break it down. YOMA nudges you when you drift.
          </p>
        </div>

        {activeNudges.length > 0 && (
          <section className="space-y-2">
            {activeNudges.map((n) => (
              <Card key={n.id} className="border-primary/30 bg-primary/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-foreground flex-1">{n.message}</p>
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                    onClick={() => dismissNudge(n.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : goals.length === 0 ? (
          <Card className="border-dashed border-border/50">
            <CardContent className="p-12 text-center">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-foreground">Your vision board is empty</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                Add your first long-term goal. Attach a vision image. Break it into milestones you can actually finish.
              </p>
              <Button className="mt-6 rounded-full gap-2" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" /> Create your first goal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <section className="grid gap-6 sm:grid-cols-2">
            {goals.map((goal) => {
              const ms = milestonesByGoal[goal.id] || [];
              const daysLeft = goal.target_date
                ? Math.ceil((new Date(goal.target_date).getTime() - Date.now()) / 86400000)
                : null;
              return (
                <Card key={goal.id} className="overflow-hidden border-border/50 hover:border-primary/30 transition-all">
                  {goal.vision_image_url && (
                    <div className="h-40 bg-muted overflow-hidden">
                      <img src={goal.vision_image_url} alt={goal.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    </div>
                  )}
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {goal.category && <Badge variant="secondary" className="text-xs">{goal.category}</Badge>}
                          {goal.status === "completed" && (
                            <Badge className="text-xs bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20">Achieved</Badge>
                          )}
                        </div>
                        <h3 className="font-display font-semibold text-foreground text-lg leading-tight">{goal.title}</h3>
                        {goal.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{goal.description}</p>
                        )}
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteGoal(goal.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{ms.filter((m) => m.completed).length} / {ms.length} milestones</span>
                        <span>{goal.progress}%</span>
                      </div>
                      <Progress value={goal.progress} className="h-2" />
                    </div>

                    {ms.length > 0 && (
                      <div className="space-y-1.5">
                        {ms.map((m) => (
                          <button key={m.id}
                            onClick={() => toggleMilestone(m)}
                            className="flex items-center gap-2 w-full text-left text-sm hover:bg-muted/50 rounded-md p-1.5 transition-colors">
                            {m.completed
                              ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                              : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                            <span className={m.completed ? "text-muted-foreground line-through" : "text-foreground"}>
                              {m.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        {goal.target_date ? (
                          <>
                            <Calendar className="h-3 w-3" />
                            {daysLeft !== null && daysLeft >= 0
                              ? `${daysLeft} days left`
                              : `Overdue by ${Math.abs(daysLeft ?? 0)} days`}
                          </>
                        ) : (
                          <>Created {formatDistanceToNow(new Date(goal.created_at), { addSuffix: true })}</>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-7"
                        onClick={() => requestNudge(goal.id)} disabled={nudgingId === goal.id}>
                        {nudgingId === goal.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <MessageCircle className="h-3 w-3" />}
                        Ask YOMA
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
